import * as Sentry from '@sentry/vue'
import * as stripe from '@stripe/stripe-js'
import moment from 'moment'
import { defineStore } from 'pinia'
import { RouteLocationRaw } from 'vue-router'

import api from '@/api/api'
import {
  StorePerformance,
  StorePerformanceSeatingTime,
  StorePromotionCode,
  StoreReservation,
  parsePerformance,
  parseReservation,
} from '@/models'
import {
  EnrichedAddOn,
  EnrichedTable,
  OrderPricing,
  getPricing,
  priceAddOns,
  tablesForSeatedParty,
} from '@/orders'
import { useSettingStore } from '@/stores/setting'
import { StoreUser, useUserStore } from '@/stores/user'
import { ErrorData, clearError, enhanceStripeError } from '@/utils/errors'
import { gtmEvt } from '@/utils/utils'

import {
  FeatureFlag,
  PerformanceAddOnRead,
  PerformanceSeatingTimeId,
  PerformanceTableId,
  PromotionCodeRead,
  ReservationAddOnWrite,
  ReservationCreate,
} from '@generated/types'

export interface Checkout {
  reservation: StoreReservation | null
  performance: StorePerformance | null
  tables: number[]
  seating_time: { id: PerformanceSeatingTimeId; time: string; datetime: string } | null
  number_of_guests: number
  add_ons: ReservationAddOnWrite[] | null
  contact: Contact
  promo_code: PromotionCodeRead | null
  scroll_position_from_set_show: number | null
  client_secret: string | null
  is_desktop_flow: boolean
}

export interface Contact {
  email: string
  phone: string
  first_name: string
  last_name: string
  accepts_marketing: boolean
}

export const useCheckoutStore = defineStore({
  id: 'checkout',
  persist: true,
  state: (): Checkout => {
    return {
      performance: null,
      number_of_guests: -1,
      seating_time: null,
      tables: [],
      add_ons: null,
      reservation: null,
      contact: {
        email: '',
        phone: '',
        first_name: '',
        last_name: '',
        accepts_marketing: true,
      } as Contact,
      promo_code: null,
      scroll_position_from_set_show: null,
      client_secret: null,
      is_desktop_flow: false,
    }
  },
  getters: {
    nextRoute(): RouteLocationRaw {
      if (this.isCheckoutReady) {
        return { name: 'checkout' }
      } else if (this.isAddOnsReady) {
        return { name: 'setAddOns' }
      } else if (this.isSetSeatingTimeReady) {
        return { name: 'setSeatingTime' }
      } else if (this.isSetTablesReady) {
        return { name: 'setTable' }
      } else {
        return { name: 'setGuests' }
      }
    },
    createReservationData(): ReservationCreate | null {
      if (!this.performance || !this.seating_time) {
        return null
      }
      const result: ReservationCreate = {
        first_name: this.contact.first_name,
        last_name: this.contact.last_name,
        email: this.contact.email,
        phone: this.contact.phone,
        accepts_marketing: this.contact.accepts_marketing || false,
        performance: this.performance.id,
        number_of_guests: this.number_of_guests,
        tables: this.tables,
        seating_time: this.seating_time.id,
        guest_notes: '',
        internal_notes: '',
        amount: this.pricing.amount.toFixed(2),
        add_on_amount: this.pricing.addOnAmount.toFixed(2),
        service_fee: this.pricing.serviceFee.toFixed(2),
        tax: this.pricing.tax.toFixed(2),
        discounted: this.pricing.discounted.toFixed(2),
      }
      if (this.promo_code) result.promo_code = this.promo_code.code
      if (this.add_ons) result.add_ons = this.add_ons
      return result
    },
    activeAddOns(): PerformanceAddOnRead[] {
      return (this.performance?.add_ons || []).filter(a => a.is_active)
    },
    hasAddOns(): boolean {
      return this.activeAddOns.length > 0
    },
    hasSelectedAddOns(): boolean {
      return this.add_ons !== null || !this.hasAddOns
    },
    hasSelectedSeatingTime(): boolean {
      return !!this.seating_time && this.seating_time?.id > 0
    },
    isSetGuestsReady(): boolean {
      return !!this.performance
    },
    isSetTablesReady(): boolean {
      return this.isSetGuestsReady && this.number_of_guests > 0
    },
    isSetSeatingTimeReady(): boolean {
      return this.isSetTablesReady && this.tables && this.tables.length > 0
    },
    isAddOnsReady(): boolean {
      return this.isSetSeatingTimeReady && this.hasSelectedSeatingTime && this.hasAddOns
    },
    isCheckoutReady(): boolean {
      return this.isSetSeatingTimeReady && this.hasSelectedSeatingTime && this.hasSelectedAddOns
    },
    isDetailsReady(): boolean {
      return this.isCheckoutReady && !!this.reservation?.id
    },
    hasPromoCodeApplied(): boolean {
      if (!this.promo_code) return false
      return true
    },
    hasSufficientGuestInfo(): boolean {
      return (
        /\S+@\S+\.\S+/.test(this.contact.email) &&
        this.contact.first_name.length > 1 &&
        this.contact.last_name.length > 1 &&
        this.contact.phone.length > 1
      )
    },
    requiresPayment(): boolean {
      return this.pricing.total > 0.5
    },
    tableModels(): EnrichedTable[] {
      return tablesForSeatedParty(this.performance, this.tables, this.number_of_guests)
    },
    addOnsModels(): EnrichedAddOn[] {
      if (!this.performance?.add_ons) return []
      return priceAddOns(this.performance.add_ons, this.add_ons)
    },
    percentOff(): number {
      return this.promo_code?.coupon?.percent_off || 0
    },
    // eslint-disable-next-line no-unused-vars
    discount(): (_: number) => number {
      // if this.percentOff is 0, then this becomes value * (1 - 0/100)
      return (value: number) => value * (1 - this.percentOff / 100)
    },
    pricing(): OrderPricing {
      return getPricing(
        this.tableModels,
        this.addOnsModels,
        this.number_of_guests,
        this.actualServiceFee,
        this.actualServicePercentage,
        useSettingStore().tax_rate,
        useSettingStore().fee_tax_rate,
        this.percentOff,
      )
    },
    scrollPositionFromSetShow(): number | null {
      return this.scroll_position_from_set_show
    },
    isDesktopFlow(): boolean {
      return this.is_desktop_flow
    },
    standingRoomOnly(): boolean {
      return this.performance?.tables?.length === 1 && this.performance?.tables[0].standing_room
    },
    actualServiceFee(): number {
      const settingStore = useSettingStore()
      return settingStore.service_fee
    },
    actualServicePercentage(): number {
      const settingStore = useSettingStore()
      return settingStore.service_percentage
    },
  },
  actions: {
    clearAddOns() {
      this.add_ons = null
      this.clearReservation()
    },
    clearReservation() {
      this.reservation = null
      this.promo_code = null
    },
    clearSeatingTime() {
      this.seating_time = null
      this.clearReservation()
    },
    clearTables() {
      this.tables = []
      this.clearSeatingTime()
    },
    clearNumberOfGuests() {
      this.number_of_guests = 0
      this.clearTables()
    },

    async refreshPerformance() {
      if (!this.performance?.id) throw new Error('No performance id is set')
      const response = await api.getPerformance(this.performance.id)
      this.performance = parsePerformance(response.data)
    },

    async setPerformance(performance: StorePerformance, error: ErrorData) {
      clearError(error)
      gtmEvt('setPerformance')
      try {
        if (performance.sold) {
          error.code = 'PERFORMANCE_SOLD_OUT'
          error.message = 'That performance is sold out.'
          return false
        }
        const settingStore = useSettingStore()
        if (moment() > settingStore.parseMoment(performance.datetime)) {
          error.code = 'PAST_PERFORMANCE'
          error.message = 'That performance is in the past.'
          return false
        }
        this.performance = performance
        if (settingStore.max_guests == 1) {
          await this.refreshPerformance()
          // for livestreaming venues we don't ask for number of guests, just assume 1
          return await this.setNumberOfGuests(1, error)
        } else {
          // if we are not livestreaming, we ask for number of guests every time
          this.clearNumberOfGuests()
          await this.$router.push(this.nextRoute)
          return true
        }
      } catch (newError) {
        console.error(newError)
        Sentry.captureException(newError)
        error.code = 'UNKNOWN'
        error.message = 'An error occured'
        return false
      }
    },
    async setNumberOfGuests(numberOfGuests: number, error: ErrorData) {
      clearError(error)
      gtmEvt('setNumberOfGuests')
      this.number_of_guests = numberOfGuests
      if (this.standingRoomOnly) {
        // if there is only one table and it is standing room only, we skip table selection
        return await this.setTables([this.performance!.tables![0].id], error)
      } else {
        this.clearTables()
        await this.$router.push(this.nextRoute)
        return true
      }
    },
    async setTables(tables: PerformanceTableId[], error: ErrorData) {
      clearError(error)
      gtmEvt('setTables')
      this.tables = tables
      if (
        useSettingStore().flagEnabled(FeatureFlag.SKIP_SEATING_TIME) &&
        this.performance?.seating_times?.length === 1
      ) {
        return await this.setSeatingTime(this.performance.seating_times[0], error)
      } else {
        this.clearSeatingTime()
        await this.$router.push(this.nextRoute)
        return true
      }
    },

    async setSeatingTime(seatingTime: StorePerformanceSeatingTime, error: ErrorData) {
      clearError(error)
      gtmEvt('setSeatingTime')
      if (seatingTime.sold) {
        error.code = 'SEATING_TIME_SOLD_OUT'
        error.message = 'That seating time is sold out.'
        return false
      }
      this.seating_time = seatingTime
      this.clearAddOns()
      await this.$router.push(this.nextRoute)
      return true
    },
    async setAddOns(addOns: any[], error: ErrorData) {
      clearError(error)
      gtmEvt('setAddOns')
      this.add_ons = addOns
      this.clearReservation()
      await this.$router.push(this.nextRoute)
      return true
    },

    setUser(user: StoreUser) {
      this.contact.first_name = user.first_name || this.contact.first_name
      this.contact.last_name = user.last_name || this.contact.last_name
      this.contact.email = user.email || this.contact.email
      this.contact.phone = user.phone || this.contact.phone
    },
    setPromoCode(promo: StorePromotionCode | null) {
      if (!promo) this.promo_code = null
      this.promo_code = promo
    },
    async validatePromotionCode(code: string): Promise<PromotionCodeRead> {
      return (await api.validatePromotionCode(code)).data
    },
    setScrollPositionFromSetShow(position: number | null) {
      this.scroll_position_from_set_show = position
    },

    async ensureReservation() {
      if (!this.client_secret || !this.reservation) {
        console.log('Creating a new reservation')
        const data = this.createReservationData
        if (!data) {
          throw new Error('Missing required information')
        }
        const createResponse = await api.addReservation(data)
        this.client_secret = createResponse.data.client_secret
        this.reservation = parseReservation(createResponse.data.reservation)
        if (!useUserStore().isLoggedIn && createResponse.data.reservation.contact) {
          try {
            window.hj('identify', `g-${createResponse.data.reservation.contact.id}`, {
              email: this.contact.email,
            })
          } catch {
            // shrug
          }
        }
      } else {
        console.log('Retrying an existing reservation')
        gtmEvt('retryReservation')
        await api.retryReservation(this.reservation.id)
      }
    },

    handleStripeResponse(stripeResp: stripe.PaymentIntentResult) {
      if (stripeResp.error) {
        gtmEvt('paymentFailure')
        throw enhanceStripeError(stripeResp.error)
      } else {
        gtmEvt('paymentSuccess')
      }
    },

    async checkout(callback: () => Promise<stripe.PaymentIntentResult>) {
      await this.ensureReservation()
      if (this.requiresPayment) {
        this.handleStripeResponse(await callback())
      }
      this.setPromoCode(null)
      await this.$router.push('/order-details')

      if (!window.dataLayer || !this.performance) return
      window.dataLayer.push({ ecommerce: null }) // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          currency: useSettingStore().venue_currency,
          transaction_id: this.reservation?.id,
          value: this.pricing.total,
          items: [
            {
              item_id: this.performance.id,
              item_name: this.performance.show.name,
              item_variant: this.tableModels.map(t => t.name).join(', '),
              price: this.pricing.total,
            },
          ],
        },
      })
    },

    setIsDesktopFlow(isDesktopFlow: boolean) {
      this.is_desktop_flow = isDesktopFlow
    },
  },
})
